import { useState, useEffect, useCallback } from 'react';
import {
  EditOutlined,
  FormOutlined,
  CreditCardOutlined,
  ProfileOutlined,
} from '@ant-design/icons';
import { AModal, ASwitch, AInput } from '../../../components';
import { Card, Form, Space, Typography } from 'antd';
import { useToast } from '../../../hooks/toast';
import {
  schemaPaymentMethodCreate,
  schemaPaymentMethodEdit,
} from '../schemas/yup.payment-method.schema';
import { IPaymentMethod } from '../../../shared/interfaces';
import { patchPaymentMethod, postPaymentMethod } from '../../../http';

const { Text } = Typography;

interface IPaymentMethodProps {
  open: boolean;
  close: () => void;
  paymentMethod: IPaymentMethod | null;
  setPaymentsMethods: React.Dispatch<React.SetStateAction<IPaymentMethod[]>>;
}

const PaymentMethodCreateEdit: React.FC<IPaymentMethodProps> = ({
  open,
  close,
  paymentMethod,
  setPaymentsMethods,
}) => {
  const { addToast } = useToast();
  const isEditing = !!paymentMethod;
  const [description, setDescription] = useState<string>('');
  const [status, setStatus] = useState<boolean>(false);

  const resetForm = useCallback(() => {
    setDescription('');
    setStatus(false);
  }, []);

  const handleCreate = useCallback(async () => {
    schemaPaymentMethodCreate
      .validate({
        description,
      })
      .then(async (validated) => {
        await postPaymentMethod(validated)
          .then((data) => {
            setPaymentsMethods((state) => [...state, data]);
            addToast({
              type: 'success',
              title: 'Cadastrado',
              description: 'Cadastrado com sucesso.',
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: 'Erro',
              description: error.response.data.error ?? 'Erro ao cadastrar.',
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: 'Campos em branco',
          description: err.errors[0],
        });
      });
  }, [description, setPaymentsMethods, addToast, close]);

  const handleEdit = useCallback(async () => {
    schemaPaymentMethodEdit
      .validate({
        description,
        status,
      })
      .then(async (validated) => {
        await patchPaymentMethod(paymentMethod?.id, validated)
          .then((data) => {
            setPaymentsMethods((state) =>
              state.map((e) => (e.id !== data.id ? e : data)),
            );
            addToast({
              type: 'info',
              title: 'Alterado',
              description: 'Dados alterados com sucesso.',
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: 'Erro',
              description: error.response.data.error ?? 'Erro ao Editar',
            });
          });
      });
  }, [
    description,
    status,
    paymentMethod?.id,
    setPaymentsMethods,
    addToast,
    close,
  ]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (paymentMethod) {
      setDescription(paymentMethod.description);
      setStatus(paymentMethod.status);
      return;
    }

    resetForm();
  }, [open, paymentMethod, resetForm]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        isEditing ? (
          <>
            <EditOutlined /> Editar
          </>
        ) : (
          <>
            <FormOutlined /> Cadastrar
          </>
        )
      }
      onOk={isEditing ? handleEdit : handleCreate}
      width={620}
    >
      <Form layout="vertical" style={{ maxWidth: '100%', marginTop: 8 }}>
        <Card size="small" style={{ marginBottom: isEditing ? 12 : 0 }}>
          <Form.Item
            label={
              <Space size={6}>
                <CreditCardOutlined style={{ color: '#2f54eb' }} />
                <span>Descrição da forma de pagamento</span>
              </Space>
            }
            required
          >
            <AInput
              placeholder="Ex: Cartão de crédito, Pix, Boleto"
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Essa descrição será exibida nas operações financeiras.
          </Text>
        </Card>

        {isEditing && (
          <Card size="small">
            <Form.Item label="Status" style={{ marginBottom: 0 }}>
              <Space align="center" size={10}>
                <ASwitch
                  checked={status}
                  checkedChildren="Sim"
                  unCheckedChildren="Não"
                  onChange={() => {
                    setStatus(!status);
                  }}
                />
                <Space size={6}>
                  <ProfileOutlined style={{ color: '#13c2c2' }} />
                  <Text type="secondary">
                    {status
                      ? 'Forma de pagamento ativa'
                      : 'Forma de pagamento inativa'}
                  </Text>
                </Space>
              </Space>
            </Form.Item>
          </Card>
        )}
      </Form>
    </AModal>
  );
};

export default PaymentMethodCreateEdit;
